'use client'

import { ExpandableSearch } from '@/ui/atoms/expandable-search/ExpandableSearch'
import { cn } from '@/ui/utils/style'
import { ArrowDownWideNarrow, ArrowUpNarrowWide } from 'lucide-react'
import type { OpportunitySortField, OpportunitySourceFilter } from '../types'

const SOURCE_FILTERS: { value: OpportunitySourceFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pooled', label: 'Pooled' },
  { value: 'isolated', label: 'Isolated' },
  { value: 'elend', label: 'Mewler' },
  { value: 'usdxl', label: 'USDXL' },
]

const SORT_FIELDS: { value: OpportunitySortField; label: string }[] = [
  { value: 'spread', label: 'Spread' },
  { value: 'maxTrade', label: 'Max Trade' },
  { value: 'borrowAPY', label: 'Borrow APY' },
  { value: 'depositAPY', label: 'Deposit APY' },
]

interface OpportunitiesFilterBarProps {
  sourceFilter: OpportunitySourceFilter
  onSourceFilterChange: (filter: OpportunitySourceFilter) => void
  sortField: OpportunitySortField
  onSortFieldChange: (field: OpportunitySortField) => void
  sortAsc: boolean
  onSortAscChange: (asc: boolean) => void
  searchQuery: string
  onSearchChange: (query: string) => void
}

export function OpportunitiesFilterBar({
  sourceFilter,
  onSourceFilterChange,
  sortField,
  onSortFieldChange,
  sortAsc,
  onSortAscChange,
  searchQuery,
  onSearchChange,
}: OpportunitiesFilterBarProps) {
  return (
    <div className="sticky top-12 z-20 flex flex-wrap items-center justify-between gap-2 bg-background py-2 sm:static sm:gap-4 sm:bg-transparent sm:py-0">
      {/* Source filter pills */}
      <div className="no-scrollbar flex items-center gap-2 overflow-x-auto">
        {SOURCE_FILTERS.map((filter) => (
          <FilterPill
            key={filter.value}
            selected={sourceFilter === filter.value}
            onClick={() => onSourceFilterChange(filter.value)}
          >
            {filter.label}
          </FilterPill>
        ))}
      </div>

      {/* Sort + Search */}
      <div className="flex items-center gap-2">
        {/* Sort pills - desktop */}
        <div className="hidden items-center gap-1.5 sm:flex">
          {SORT_FIELDS.map((field) => (
            <FilterPill
              key={field.value}
              selected={sortField === field.value}
              onClick={() => onSortFieldChange(field.value)}
              size="sm"
            >
              {field.label}
            </FilterPill>
          ))}
          <button
            type="button"
            onClick={() => onSortAscChange(!sortAsc)}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground"
            title={sortAsc ? 'Low to High' : 'High to Low'}
          >
            {sortAsc ? <ArrowUpNarrowWide className="h-3.5 w-3.5" /> : <ArrowDownWideNarrow className="h-3.5 w-3.5" />}
          </button>
        </div>

        <ExpandableSearch value={searchQuery} onChange={onSearchChange} size="sm" />
      </div>
    </div>
  )
}

function FilterPill({
  children,
  selected,
  onClick,
  size = 'default',
}: {
  children: React.ReactNode
  selected: boolean
  onClick: () => void
  size?: 'sm' | 'default'
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'shrink-0 rounded-full font-medium transition-colors',
        size === 'sm' ? 'px-2 py-1 text-[11px]' : 'px-3 py-1.5 text-xs',
        selected
          ? 'bg-primary text-primary-foreground'
          : 'bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-foreground',
      )}
    >
      {children}
    </button>
  )
}
